import * as React from "react"
import { Drawer as DrawerPrimitive } from "vaul"
import { CheckIcon, XIcon } from "@phosphor-icons/react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

type SheetSide = "top" | "right" | "bottom" | "left"

/**
 * A `Sheet` a vaul Drawer-en ül (natív húzással záródik), a megszokott shadcn
 * Sheet API-val: a `SheetContent` `side` propja adja az irányt, a `SheetTopBar`
 * a mobil dialógusok fejléce (✕ balra / cím középen / ✓ jobbra). A vaul a
 * `direction`-t a gyökéren várja, ezért a `Sheet` a gyerek `SheetContent`
 * `side`-jából vezeti le.
 */

/** A gyerek `SheetContent` `side` propját keresi meg (Fragmenten át is), hogy a
 *  vaul gyökér a megfelelő `direction`-t kapja. Explicit `side` nélkül `bottom`. */
function findSheetSide(children: React.ReactNode): SheetSide | undefined {
  let found: SheetSide | undefined
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return
    if (child.type === SheetContent) {
      found = (child.props as { side?: SheetSide }).side ?? "bottom"
    } else if (child.type === React.Fragment) {
      found = findSheetSide((child.props as { children?: React.ReactNode }).children)
    }
  })
  return found
}

/** Jelzi, hogy egy `Sheet` egy másik `Sheet` fáján belül van-e. Ekkor a vaul
 *  `NestedRoot`-ját használjuk a sima `Root` helyett, különben a két egymásba
 *  ágyazott fiók a body scroll-lockon osztozva ütközik (a belső bezárása a külső
 *  scroll-lockját törné el). */
const SheetNestingContext = React.createContext(false)

function Sheet({
  children,
  direction,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  const isNested = React.useContext(SheetNestingContext)
  // Az explicit `direction` propot tiszteljük, különben a gyerek SheetContent
  // side-jából vezetjük le.
  const resolvedDirection = direction ?? findSheetSide(children) ?? "bottom"
  const Root = isNested ? DrawerPrimitive.NestedRoot : DrawerPrimitive.Root
  return (
    <SheetNestingContext.Provider value={true}>
      {/* A vaul `repositionInputs` alapértelmezését (igaz) MEGTARTJUK: az alsó lap
          `position: fixed`, tehát a felugró mobil billentyűzet különben elfedné a fókuszált
          mezőt (jelszó, "Zárás oka" - a lapjaink java része űrlap). */}
      <Root data-slot="sheet" {...props} direction={resolvedDirection}>
        {children}
      </Root>
    </SheetNestingContext.Provider>
  )
}

function SheetTrigger(props: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose(props: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return (
    <DrawerPrimitive.Portal data-slot="sheet-portal" {...props}>
      {children}
    </DrawerPrimitive.Portal>
  )
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn("fixed inset-0 z-50 bg-black/30 backdrop-blur-sm", className)}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "bottom",
  showCloseButton = true,
  overlayClassName,
  initialFocus,
  ref,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content> & {
  side?: SheetSide
  showCloseButton?: boolean
  overlayClassName?: string
  /**
   * A Base UI `DialogContent` `initialFocus`-ával azonos alakú, hogy a közös
   * `ResponsiveDialog` mindkét ágnak ugyanazt adhassa: ref = az adott elem kapja a
   * fókuszt nyitáskor; `false` vagy elhagyva = a panel konténere (lásd lent).
   */
  initialFocus?: unknown
}) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  // A hívó `ref`-jét is kiszolgáljuk, de a sajátunkat mindenképp megtartjuk: az
  // `initialFocus={false}` ág ezen a csomóponton veszi át a fókuszt.
  const setContentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  const handleOpenAutoFocus = React.useMemo<(event: Event) => void>(() => {
    if (initialFocus && typeof initialFocus === "object" && "current" in initialFocus) {
      const ref = initialFocus as React.RefObject<HTMLElement | null>
      return (event) => {
        event.preventDefault()
        ref.current?.focus()
      }
    }
    // Alapértelmezés (és `initialFocus={false}`): a fókusz a tartalom konténerére megy
    // (tabindex=-1, nem input). A vaul `autoFocus` alapból hamis, ezért maga is
    // `preventDefault`-ol - fókusz nélkül a trigger tartaná meg, miközben a hátteret
    // `aria-hidden`-né teszi ("Blocked aria-hidden ... descendant retained focus"), ami
    // valódi a11y hiba. Konténerre fókuszálva a háttér biztonságosan elrejthető, a mobil
    // billentyűzet pedig nem ugrik fel, és a Tab innen lépked végig a panel elemein.
    // (A vaul a mi kezelőnket HÍVJA MEG ELŐSZÖR, csak utána `preventDefault`-ol.)
    return (event) => {
      event.preventDefault()
      contentRef.current?.focus({ preventScroll: true })
    }
  }, [initialFocus])

  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <DrawerPrimitive.Content
        ref={setContentRef}
        data-slot="sheet-content"
        onOpenAutoFocus={handleOpenAutoFocus}
        className={cn(
          "group/sheet-content fixed z-50 flex flex-col bg-popover text-popover-foreground shadow-lg outline-none",
          // A `side` JS-ben ismert (= a vaul direction), ezért előtag nélküli osztályok:
          // így a tailwind-merge dedupálja a hívó felülírásait (egy `max-h-[90vh]`
          // valóban felülírja a 85vh alapot).
          side === "right" && "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" && "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" && "inset-x-0 top-0 h-auto rounded-b-2xl border-b",
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto max-h-[85vh] rounded-t-2xl border-t pb-[env(safe-area-inset-bottom,0px)]",
          className
        )}
        {...props}
      >
        {/* Fogantyú csak alsó fióknál - a vaul az egész tartalmat húzhatóvá teszi
            (a görgetés tetején vált húzásra), ez csak vizuális jelzés. */}
        {side === "bottom" && (
          <div
            aria-hidden="true"
            className="mx-auto mt-3 mb-1 h-1.5 w-12 shrink-0 rounded-full bg-muted-foreground/40"
          />
        )}
        {children}
        {showCloseButton && side !== "bottom" && (
          <DrawerPrimitive.Close data-slot="sheet-close" asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 transition-transform hover:scale-110 active:scale-95"
            >
              <XIcon />
              <span className="sr-only">Bezárás</span>
            </Button>
          </DrawerPrimitive.Close>
        )}
      </DrawerPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

/**
 * A mobil dialógus fejléce: ✕ balra, cím középen, ✓ (vagy üres hely) jobbra.
 * A `close` / `action` kész gombot vesz át (a `ResponsiveDialog` a lábléc
 * gombjait emeli ide), az `onClose` / `onAction` pedig a saját alap gombjait
 * rakja ki; `onClose` nélkül a ✕ a fiók saját bezárója.
 */
function SheetTopBar({
  className,
  title,
  description,
  close,
  closeLabel = "Bezárás",
  closeDisabled,
  actionLabel = "Alkalmaz",
  action,
  actionIcon,
  actionDisabled,
  onAction,
  onClose,
  titleClassName,
  descriptionClassName,
  ...props
}: Omit<React.ComponentProps<"div">, "title"> & {
  title: React.ReactNode
  description?: React.ReactNode
  close?: React.ReactNode
  closeLabel?: string
  closeDisabled?: boolean
  actionLabel?: string
  action?: React.ReactNode
  actionIcon?: React.ReactNode
  actionDisabled?: boolean
  onAction?: () => void
  onClose?: () => void
  titleClassName?: string
  descriptionClassName?: string
}) {
  const closeButton =
    close ??
    (onClose ? (
      <Button
        type="button"
        variant="secondary"
        size="icon-lg"
        className="rounded-full transition-transform hover:scale-110 active:scale-95"
        disabled={closeDisabled}
        onClick={onClose}
      >
        <XIcon />
        <span className="sr-only">{closeLabel}</span>
      </Button>
    ) : (
      <DrawerPrimitive.Close data-slot="sheet-close" asChild>
        <Button
          type="button"
          variant="secondary"
          size="icon-lg"
          className="rounded-full transition-transform hover:scale-110 active:scale-95"
          disabled={closeDisabled}
        >
          <XIcon />
          <span className="sr-only">{closeLabel}</span>
        </Button>
      </DrawerPrimitive.Close>
    ))

  const actionButton =
    action ??
    (onAction ? (
      <Button
        type="button"
        variant="default"
        size="icon-lg"
        className="rounded-full transition-transform hover:scale-110 active:scale-95"
        disabled={actionDisabled}
        onClick={onAction}
      >
        {actionIcon ?? <CheckIcon weight="bold" />}
        <span className="sr-only">{actionLabel}</span>
      </Button>
    ) : (
      <div className="size-10" aria-hidden="true" />
    ))

  return (
    <>
      <div
        data-slot="sheet-top-bar"
        className={cn(
          "grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-3 px-4 pb-3",
          className
        )}
        {...props}
      >
        <div className="flex items-center justify-start">{closeButton}</div>
        <div className="min-w-0 text-center">
          <SheetTitle className={cn("truncate", titleClassName)}>{title}</SheetTitle>
        </div>
        <div className="flex items-center justify-end">{actionButton}</div>
      </div>
      {description != null && description !== "" && (
        <SheetDescription className={cn("sr-only", descriptionClassName)}>
          {description}
        </SheetDescription>
      )}
    </>
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-base font-medium text-foreground", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="sheet-description"
      // Látható alapból (natív shadcn): a fejléc alatti magyarázó sor. Ahol csak
      // képernyőolvasónak szól (`SheetTopBar`, `ResponsiveDialog`), a hívó ad
      // `sr-only`-t. Az `aria-describedby` bekötése a primitíven van, nem itt.
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTopBar,
  SheetTitle,
  SheetDescription,
}
